import * as React from 'react';
import { memo, useMemo, useState } from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';
import { localeData } from '../../../locales/data';
import { DatasetDescriptionData, DatasetDescriptionResponse } from '../../../shared/api/api';
import { getIsFetchNotFoundError } from '../../../shared/api/utils/fetchErrors';

import { UseQueryResult } from 'react-query';
import { getRegionStatsTableColumns } from './RegionTableColumns';
import { getColumnTransform } from '../DataTableCommon/DataTableCommon';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import noop from 'lodash/noop';
import { useWatchCommonDataFields } from '../CommonDataFieldsSelector/CommonDataFieldsSelector';
import { addModifiers } from '../../../shared/helpers';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { useSingleEntityDataTable } from '../DataTableCommon/useSingleEntityDataTable';
import {
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { RegionTableMarkup } from './RegionTableMarkup';
import { useSelector } from 'react-redux';
import { hasConsultantOrResearcherPermission } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';

type Props = {
  datasetDescriptionResult: UseQueryResult<DatasetDescriptionResponse, Error>;
  control: FiltersControl;
} & InjectedIntlProps;
const grouping = ['region'];

const DatasetRegionsTableWithoutIntl = ({ intl, datasetDescriptionResult, control }: Props) => {
  const { isFetching, isError, data } = datasetDescriptionResult;
  const transformColumn = useMemo(
    () =>
      getColumnTransform<DatasetDescriptionData>({
        intl,
        sortOrder: { key: 'none', isAscending: true },
        handleOrderChange: noop,
        isOrderDisabled: true,
      }),
    [intl]
  );
  const trackEvent = useDataExplorerTrackCustomEvent();
  const isPyramidMultiplierVisible = useSelector(hasConsultantOrResearcherPermission);

  const tableColumns = useMemo(() => {
    const columns = getRegionStatsTableColumns<DatasetDescriptionData>(trackEvent, isPyramidMultiplierVisible);
    return columns.map(transformColumn);
  }, [transformColumn, trackEvent]);
  const selectedFilters = useWatchCommonDataFields(control);

  const {
    getTrProps,
    isTableExpanded,
    expandedRows,
    setExpandedRows,
    activeFieldData,
    toggleTableExpanded,
    dataToRender,
    isExpandVisible,
  } = useSingleEntityDataTable(control, data?.data);

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    columns: tableColumns,
    data: dataToRender || [],
    state: {
      grouping,
      expanded: expandedRows,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualExpanding: true,
  });

  const headerGroups = table.getHeaderGroups();

  // show loader when data load for the first time
  if (isFetching && !data) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (isError || !data) {
    const errorMessageId = getIsFetchNotFoundError(datasetDescriptionResult.error)
      ? 'dataExplorer.datasetTableError.notFound'
      : 'dataExplorer.datasetTableError';

    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessageHelper id={errorMessageId} />
          </div>
        </div>
      </div>
    );
  }

  const toggleButtonTextKey = isTableExpanded ? 'dataExplorer.showLess' : 'dataExplorer.showMore';

  function getExpanderClickHandler(rowId: string) {
    return () => {
      if (rowId in expandedRows) {
        setExpandedRows(prevState => ({ ...expandedRows, [rowId]: !prevState[rowId] }));
      } else {
        setExpandedRows(prevState => ({ ...expandedRows, [rowId]: true }));
      }
    };
  }

  return (
    <div className={addModifiers('data-table__container data-single-entity-table', [isTableExpanded ? 'expanded' : ''])}>
      <h2>
        <FormattedMessageHelper id="dataExplorer.dataset.stats" />
      </h2>

      {!activeFieldData && (
        <div className={'data-single-entity__not-found-note'}>
          <FormattedMessageHelper
            tagName="span"
            id={'dataExplorer.datasetDataNotFound'}
            defaultMessage={localeData.en['dataExplorer.datasetDataNotFound']}
            values={{ region: selectedFilters.region, delay: selectedFilters.delay, universe: selectedFilters.universe }}
          />
        </div>
      )}
      <div className="data-single-entity__table-container agreements__content full-height">
        <RegionTableMarkup<DatasetDescriptionData>
          data={data?.data}
          isFetching={isFetching}
          headerGroups={headerGroups}
          sorting={sorting}
          table={table}
          getTrProps={getTrProps}
          isTableExpanded={isTableExpanded}
          getExpanderClickHandler={getExpanderClickHandler}
          isExpandVisible={isExpandVisible}
          toggleTableExpanded={toggleTableExpanded}
          toggleButtonTextKey={toggleButtonTextKey}
        />
      </div>
    </div>
  );
};
interface ExpanderProps {
  isOpen: boolean;
}
export function Expander({ isOpen }: ExpanderProps) {
  return <div className={`rt-expander ${isOpen ? '-open' : ''}`}>•</div>;
}

export const DatasetRegionsTable = injectIntl(memo(DatasetRegionsTableWithoutIntl));
